import * as React from 'react';

import { cn } from '../../utils';

export interface SwitchProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'type'
> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * Lightweight toggle switch. Controlled, mirrors the Checkbox API
 * (`checked` / `onCheckedChange`) for drop-in familiarity. No Radix dependency —
 * a native button with `role="switch"`, styled with the brand blue when on.
 */
const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onCheckedChange, disabled, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      data-state={checked ? 'checked' : 'unchecked'}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-[22px] w-10 flex-none cursor-pointer items-center rounded-full border border-border bg-muted transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-riso-blue data-[state=checked]:bg-riso-blue',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'absolute left-0.5 h-4 w-4 rounded-full bg-white shadow-hard-sm transition-transform',
          checked && 'translate-x-[18px]',
        )}
      />
    </button>
  ),
);
Switch.displayName = 'Switch';

export { Switch };
